import { AxiosResponse } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { User } from "@/models/user";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import { UserSchema } from "@/services/users/users.api.types";
import UserEndpoints from "@/services/users/users.endpoint";

export class UserService {
  static getUserList(): {
    data: User[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<UserSchema[], undefined>>;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, UserSchema[]>({
        url: UserEndpoints.GET_USER_LIST,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      UserEndpoints.GET_USER_LIST,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.map(buildUser),
      error,
      isLoading,
      mutate,
    };
  }

  static async createUser(email: string, name: string, role: string) {
    await ApiService.fetchData<
      { email: string; name: string; role: string; },
      UserSchema
    >({
      url: UserEndpoints.CREATE_USER,
      method: "POST",
      data: { email, name, role },
    });
  }

  static async updateUser(userId: string, name: string, role: string) {
    await ApiService.fetchData<{ name: string; role: string; }, UserSchema>({
      url: UserEndpoints.UPDATE_USER(userId),
      method: "PUT",
      data: { name, role },
    });
  }

  static async deleteUser(userId: string) {
    await ApiService.fetchData<void, void>({
      url: UserEndpoints.DELETE_USER(userId),
      method: "DELETE",
    });
  }
}

const buildUser = (user: UserSchema) => new User(user.id, user.name, user.email, user.role);
